/**
 * Dates, times, numbers, percentages and prices, in the page's language.
 *
 * One module, because each front end instantiating `Intl` its own way is how
 * the tutor app came to print "1 Sep" while the administration printed
 * "1 sept 2026" for the same day, and how a price read "15 US$" on one screen
 * and "US$15" on another. Every function takes the language explicitly and
 * never passes `undefined` to `Intl` — that is the browser's locale, not the
 * page's, and a Spanish page on an English laptop would print English dates.
 *
 * The conventions, per language:
 *
 * | What            | es (es-CR)                    | en (en-US)             |
 * | --------------- | ----------------------------- | ---------------------- |
 * | Date            | 29 sept 2026                  | Sep 29, 2026           |
 * | Date and time   | 29 sept 2026, 10:30 a. m.     | Sep 29, 2026, 10:30 AM |
 * | Time            | 10:30 a. m.                   | 10:30 AM               |
 * | Day, no year    | 29 sept                       | Sep 29                 |
 * | Range           | 29 de sept – 3 de oct de 2026 | Sep 29 – Oct 3, 2026   |
 * | Number          | 12 500                        | 12,500                 |
 * | Percentage      | 85%                           | 85%                    |
 * | File size       | 2,4 MB                        | 2.4 MB                 |
 * | List            | Tutor y Estudiante            | Tutor and Student      |
 * | Price (USD)     | US$15                         | $15                    |
 *
 * "Day, no year" is for a chart axis and a compact chip where the year is
 * implied by what surrounds it; anything a person might read back later — an
 * availability date, a submission, an audit entry — carries its year.
 *
 * No dependency: `Intl` ships in every runtime Athena targets, which is what
 * lets the API and the emails read the same conventions as the browser.
 */
import { CATALOG_LOCALES } from './language.js';
/**
 * The BCP-47 locale `Intl` formats a language with.
 *
 * The same tags the catalog reads (`language.ts`), because they are the same
 * decision: Athena's Spanish is Costa Rica's, and its English is the United
 * States'. One map, so widening to a second Spanish market is one line.
 */
export function localeFor(language) {
    return CATALOG_LOCALES[language];
}
function toDate(value) {
    return value instanceof Date ? value : new Date(value);
}
/** "29 sept 2026" / "Sep 29, 2026". */
export function formatDate(value, language) {
    return new Intl.DateTimeFormat(localeFor(language), { dateStyle: 'medium' }).format(toDate(value));
}
/** "29 sept 2026, 10:30 a. m." / "Sep 29, 2026, 10:30 AM". */
export function formatDateTime(value, language) {
    return new Intl.DateTimeFormat(localeFor(language), {
        dateStyle: 'medium',
        timeStyle: 'short',
    }).format(toDate(value));
}
/** "10:30 a. m." / "10:30 AM". */
export function formatTime(value, language) {
    return new Intl.DateTimeFormat(localeFor(language), { timeStyle: 'short' }).format(toDate(value));
}
/** A calendar day with its year: "29 sept 2026" / "Sep 29, 2026". */
export function formatDay(value, language, options = {}) {
    return new Intl.DateTimeFormat(localeFor(language), {
        dateStyle: 'medium',
        ...(options.utc ? { timeZone: 'UTC' } : {}),
    }).format(toDate(value));
}
/** A calendar day without its year, for an axis or a compact chip: "29 sept" / "Sep 29". */
export function formatShortDay(value, language, options = {}) {
    return new Intl.DateTimeFormat(localeFor(language), {
        day: 'numeric',
        month: 'short',
        ...(options.utc ? { timeZone: 'UTC' } : {}),
    }).format(toDate(value));
}
/**
 * Two dates as one range, without repeating what they share:
 * "29 de sept – 3 de oct de 2026" / "Sep 29 – Oct 3, 2026".
 */
export function formatDateRange(start, end, language) {
    return new Intl.DateTimeFormat(localeFor(language), { dateStyle: 'medium' }).formatRange(toDate(start), toDate(end));
}
/**
 * "hoy", "mañana", "en 3 días" / "today", "tomorrow", "in 3 days" — counted in
 * calendar days, not elapsed hours. 09:00 tomorrow is nineteen hours away at
 * 14:00 today, which rounds to "today" by elapsed time, and that is the kind
 * of wrong that costs a deadline.
 */
export function formatRelativeDay(value, language, now = new Date()) {
    const then = toDate(value);
    const from = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const to = new Date(then.getFullYear(), then.getMonth(), then.getDate());
    const days = Math.round((to.getTime() - from.getTime()) / (24 * 60 * 60 * 1000));
    return new Intl.RelativeTimeFormat(localeFor(language), { numeric: 'auto' }).format(days, 'day');
}
/** A count or a total: "12 500" / "12,500". Whole numbers unless the value is not one. */
export function formatNumber(value, language, options = {}) {
    return new Intl.NumberFormat(localeFor(language), {
        maximumFractionDigits: 2,
        ...(options.signed ? { signDisplay: 'exceptZero' } : {}),
    }).format(value);
}
/** Several names as one phrase: "Tutor y Estudiante" / "Tutor and Student". */
export function formatList(items, language) {
    return new Intl.ListFormat(localeFor(language), { style: 'long', type: 'conjunction' }).format(items);
}
/**
 * A file's size: "850 KB", "2,4 MB" / "2.4 MB".
 *
 * The unit symbols are the same in both languages, so only the number is the
 * language's — which is the part that differs: a Spanish page writes the
 * decimal with a comma. Binary multiples, as the upload limits are computed,
 * with one decimal from a megabyte up.
 */
export function formatFileSize(bytes, language) {
    const figure = (value, digits) => new Intl.NumberFormat(localeFor(language), {
        minimumFractionDigits: digits,
        maximumFractionDigits: digits,
    }).format(value);
    if (bytes < 1024)
        return `${figure(bytes, 0)} B`;
    if (bytes < 1024 * 1024)
        return `${figure(Math.round(bytes / 1024), 0)} KB`;
    return `${figure(bytes / (1024 * 1024), 1)} MB`;
}
/**
 * A score, as a whole percentage: "85%".
 *
 * Takes the percentage (0–100), which is what every score in the API already
 * is, and rounds it for display only — the stored score is never touched. A
 * whole number because that is the precision a score is read at: "85.3%"
 * suggests a difference between two attempts that nobody should act on.
 */
export function formatPercent(percentage, language) {
    return new Intl.NumberFormat(localeFor(language), {
        style: 'percent',
        maximumFractionDigits: 0,
    }).format(percentage / 100);
}
/**
 * The symbol a currency is written with, where the locale's own is ambiguous.
 *
 * Costa Rica writes its colón as ₡ and reads a bare "$" as the US dollar, but
 * Athena's Spanish is read in markets where "$" is the local peso. "US$" is
 * the unambiguous spelling in all of them; `Intl`'s own Spanish choice is
 * "USD 15" (es-CR) or "15 US$" (es), neither of which is how a price is
 * written on a page. English keeps "$", which is what an English reader of a
 * dollar price expects.
 */
const CURRENCY_SYMBOLS = {
    es: { USD: 'US$' },
};
/**
 * A price, from the integer minor units the API carries: "US$15" / "$15".
 *
 * `Intl` still decides everything but the symbol — where it goes, the
 * grouping, the decimals — so the only rule written down here is which
 * symbol. The fraction digits are dropped when the amount is whole, because
 * every price in the catalog is: "$15" reads as a price and "$15.00" as an
 * invoice. The currency comes from the value, so a number is never rendered
 * as dollars by accident.
 */
export function formatMoney(money, language) {
    const value = money.amountMinorUnits / 100;
    const whole = Number.isInteger(value);
    const symbol = CURRENCY_SYMBOLS[language]?.[money.currency];
    const format = new Intl.NumberFormat(localeFor(language), {
        style: 'currency',
        currency: money.currency,
        currencyDisplay: symbol ? 'narrowSymbol' : 'symbol',
        minimumFractionDigits: whole ? 0 : 2,
        maximumFractionDigits: 2,
    });
    if (!symbol)
        return format.format(value);
    return format
        .formatToParts(value)
        .map((part) => (part.type === 'currency' ? symbol : part.value))
        .join('');
}
/** API estimates need sub-cent precision; ordinary subscription prices remain unchanged. */
export function formatEstimatedUsd(value, language) {
    const symbol = CURRENCY_SYMBOLS[language]?.['USD'];
    return new Intl.NumberFormat(localeFor(language), {
        style: 'currency',
        currency: 'USD',
        currencyDisplay: 'narrowSymbol',
        minimumFractionDigits: 2,
        maximumFractionDigits: 6,
    })
        .formatToParts(value)
        .map((part) => (part.type === 'currency' && symbol ? symbol : part.value))
        .join('');
}
//# sourceMappingURL=format.js.map